import { defaultMaximumPagination, RequestConfig } from 'helpers';
import { AccountType } from 'services/accounting';
import {
  createApiCall,
  DataWithPagination,
  paginatedApiCall,
} from 'services/api';
import { transformQuickbooks } from './transformations';
import { Quickbooks, QBODimensionDefinition, QBODimensionValue } from './types';

export const fetchQuickbooksAuthUrl = async (): Promise<string> => {
  const response = await createApiCall(
    {
      path: '/integrations/qbo/auth',
      method: 'GET',
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

export const exportToQuickbooks = async (): Promise<string> => {
  const response = await createApiCall(
    {
      path: '/integrations/qbo/export',
      method: 'POST',
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
        success: () => 'Export to QuickBooks successful.',
      },
    }
  )();

  return response.data;
};

export const fetchTaxExemptionCode = async (): Promise<
  {
    id: number;
    reason: string;
  }[]
> => {
  const response = await createApiCall(
    {
      path: '/integrations/qbo/taxExemptionCode',
      method: 'GET',
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

export const fetchQuickBooksAccountsAPI = async (
  config: RequestConfig,
  accountType: AccountType | null,
  listId: string[] | null
): Promise<DataWithPagination<Quickbooks>> => {
  const {
    pagination = defaultMaximumPagination,
    customQuickSearchColumns = ['name'],
    quickSearchValue,
  } = config;
  let path = `/integrations/qbo/accounts`;

  if (accountType) {
    path += `?accountType=${accountType}`;
  }

  if (listId && listId.length > 0) {
    path += (accountType ? `&` : `?`) + `listId=${listId}`;
  }

  if (quickSearchValue) {
    path +=
      (accountType || listId ? `&` : `?`) +
      `quickSearchColumns=${customQuickSearchColumns}&quickSearchValue=${quickSearchValue}`;
  }

  const res = await paginatedApiCall(path, pagination, transformQuickbooks);
  return res;
};

/**
 * Fetch QuickBooks Dimension Definitions
 * Endpoint: /v1/qbo/dimensions
 */
export const fetchQBODimensionDefinitions = async (): Promise<
  QBODimensionDefinition[]
> => {
  const response = await createApiCall(
    {
      path: '/v1/qbo/dimensions',
      method: 'GET',
    },
    {
      getMessage: {
        error: (error: any) =>
          `Failed to fetch QuickBooks dimension definitions: ${
            error.response?.data?.message || error.message
          }`,
      },
      suppressErrorNotification: true,
    }
  )();

  return response.data;
};

/**
 * Fetch QuickBooks Dimension Values for a specific dimension
 * Endpoint: /v1/qbo/dimensions/{definitionId}
 */
export const fetchQBODimensionValues = async (
  definitionId: string
): Promise<QBODimensionValue[]> => {
  const response = await createApiCall(
    {
      path: `/v1/qbo/dimensions/${definitionId}`,
      method: 'GET',
    },
    {
      getMessage: {
        error: (error: any) =>
          `Failed to fetch dimension values: ${
            error.response?.data?.message || error.message
          }`,
      },
      suppressErrorNotification: true,
    }
  )();

  return response.data;
};

/**
 * Fetch all QuickBooks Dimensions with their values
 * Combines definitions and values into the expected format
 */
export const fetchQBODimensions = async (): Promise<{
  definitions: QBODimensionDefinition[];
  allValues: QBODimensionValue[];
}> => {
  // Step 1: Get dimension definitions
  const definitions = await fetchQBODimensionDefinitions();

  // Step 2: Get values for each active definition IN PARALLEL
  const activeDefinitions = definitions.filter((def) => def.active);

  const valuePromises = activeDefinitions.map((definition) =>
    fetchQBODimensionValues(definition.id).catch((error) => {
      console.error(
        `Failed to fetch values for dimension ${definition.label}:`,
        error
      );
      return []; // Return empty array on error
    })
  );

  // Wait for all value fetches to complete in parallel
  const valuesArrays = await Promise.all(valuePromises);
  const allValues = valuesArrays.flat();

  return { definitions, allValues };
};
